import i18next from "i18next";
import { useMemo } from "react";

import { normalizePrice } from "../../../utils";

import { Card, CardLabel, CardPrice, CardRow, CardText, Title, Wrapper } from "./style";

/* Быстрый выбор над списком: оптимальный, самый дешёвый и самый быстрый из отфильтрованных
   рекомендаций. Считается по текущей валюте; если один и тот же вариант лучший по нескольким
   критериям, карточка одна с несколькими подписями. Клик — прокрутка к рекомендации в списке. */

const priceOf = (recommendation, currency) => {
  const price = recommendation?.total_price?.[currency];
  if (price !== undefined && price !== null && !Number.isNaN(Number(price))) return Number(price);
  const first = Object.values(recommendation?.total_price || {})[0];
  return first === undefined ? Infinity : Number(first);
};

const durationOf = recommendation =>
  (recommendation?.routes || []).reduce((sum, route) => sum + (Number(route?.duration) || 0), 0);

const stopsOf = recommendation =>
  Math.max(0, ...(recommendation?.routes || []).map(route => Math.max(0, (route?.segments?.length || 1) - 1)));

const formatDuration = seconds => {
  const total = Math.max(0, Math.floor(seconds / 60));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return `${hours}${i18next.t("hours_short")} ${minutes}${i18next.t("minutes_short")}`;
};

export const getQuickPicks = (recommendations, currency) => {
  const items = (recommendations || []).filter(rec => rec && rec.rec_id && Number.isFinite(priceOf(rec, currency)));
  if (items.length === 0) return [];

  const minPrice = Math.min(...items.map(rec => priceOf(rec, currency)));
  const minDuration = Math.min(...items.map(durationOf));
  const byPrice = (a, b) => priceOf(a, currency) - priceOf(b, currency) || durationOf(a) - durationOf(b);
  const byDuration = (a, b) => durationOf(a) - durationOf(b) || priceOf(a, currency) - priceOf(b, currency);
  // оптимальный: минимальная сумма относительных отклонений от лучшей цены и лучшего времени
  const score = rec => priceOf(rec, currency) / (minPrice || 1) + durationOf(rec) / (minDuration || 1);
  const byScore = (a, b) => score(a) - score(b) || byPrice(a, b);

  const winners = [
    { key: "optimal", rec: [...items].sort(byScore)[0] },
    { key: "cheapest", rec: [...items].sort(byPrice)[0] },
    { key: "fastest", rec: [...items].sort(byDuration)[0] },
  ];

  const picks = [];
  winners.forEach(({ key, rec }) => {
    const existing = picks.find(pick => pick.rec.rec_id === rec.rec_id);
    if (existing) {
      existing.keys.push(key);
    } else {
      picks.push({ keys: [key], rec });
    }
  });

  return picks;
};

const QuickPicksComponent = ({ recommendations, currency, onPick }) => {
  const { t } = i18next;
  const picks = useMemo(() => getQuickPicks(recommendations, currency), [recommendations, currency]);

  if (picks.length === 0) return null;

  return (
    <Wrapper>
      <Title>{t("quick_picks_title")}</Title>
      <CardRow>
        {picks.map(({ keys, rec }) => {
          const stops = stopsOf(rec);
          const airlines = Array.from(
            new Set((rec.routes || []).flatMap(route => (route.segments || []).map(segment => segment.operation_supplier)).filter(Boolean))
          );
          return (
            <Card key={rec.rec_id} type="button" onClick={() => onPick && onPick(rec)} title={t("quick_pick_hint")}>
              <CardLabel>{keys.map(key => t(`quick_${key}`)).join(" · ")}</CardLabel>
              <CardText>
                {airlines.join(", ") || rec.validating_supplier}
                {" · "}
                {stops === 0 ? t("no_transfers") : t("quick_transfers", { count: stops })}
              </CardText>
              <CardText>{formatDuration(durationOf(rec))}</CardText>
              <CardPrice>
                {normalizePrice(priceOf(rec, currency))} {currency}
              </CardPrice>
            </Card>
          );
        })}
      </CardRow>
    </Wrapper>
  );
};

export default QuickPicksComponent;
